import * as Sentry from '@sentry/react'
import { COOKIE_ACCESS, COOKIE_REFRESH, COOKIE_USER } from './auth.constants'

/**
 * Captura un evento de rebote de login en Sentry
 * @param {Object} params - Parámetros del evento
 * @param {string} params.tokenSent - Token enviado (enmascarado)
 * @param {Object} params.response - Información de respuesta de checkauth
 * @param {number} params.response.status - HTTP status de la respuesta
 * @param {Object} params.response.payload - Payload de respuesta
 * @param {boolean} params.authValid - Indica si la autenticación fue válida
 * @param {'warning'|'error'} params.level - Nivel del evento
 * @param {string} params.reason - Razón del rebote
 */
export const captureLoginBounceEvent = ({ tokenSent, response, authValid, level = 'warning', reason }) => {
  try {
    // Capturar cookies de frontend relacionadas al login
    const loginCookies = captureFrontendLoginCookies()

    // Construir el contexto completo del evento
    Sentry.withScope((scope) => {
      // Tags para filtrado
      scope.setTag('event_type', 'login_bounce')
      scope.setTag('auth_valid', authValid ? 'valid' : 'invalid')
      scope.setTag('redirect_target', 'www.provinciart.com.ar')
      scope.setTag('http_status', response?.status || 'unknown')
      scope.setLevel(level)

      // Contexto adicional
      scope.setContext('checkauth_request', {
        token_sent: tokenSent,
        timestamp: new Date().toISOString(),
      })

      scope.setContext('checkauth_response', {
        http_status: response?.status,
        error_code: response?.payload?.errorCode || response?.payload?.code,
        message: response?.payload?.message || response?.payload?.error,
        flags: extractRelevantFlags(response?.payload),
        full_payload: response?.payload,
      })

      scope.setContext('frontend_cookies', loginCookies)

      // Fingerprint para agrupar eventos similares
      scope.setFingerprint([
        'login-bounce',
        String(response?.status || 'unknown'),
        String(response?.payload?.errorCode || response?.payload?.code || 'no-code'),
      ])

      // Capturar el evento como mensaje
      Sentry.captureMessage(`Login bounce: ${reason || 'Authentication failed'}`, level)
    })
  } catch (error) {
    console.error('❌ Error capturando evento de rebote en Sentry:', error)
  }
}

/**
 * Enmascara un token para logging seguro
 * @param {string} token - Token a enmascarar
 * @returns {string} Token enmascarado
 */
export const maskToken = (token) => {
  if (!token || typeof token !== 'string') return 'N/A'

  if (token.length <= 8) {
    return '***'
  }

  const firstChars = token.substring(0, 4)
  const lastChars = token.substring(token.length - 4)
  return `${firstChars}...${lastChars}`
}

/**
 * Captura las cookies de frontend relacionadas al login
 * @returns {Object} Mapa de cookies con sus valores
 */
const captureFrontendLoginCookies = () => {
  const cookies = {}

  // Cookies en localStorage
  const storageKeys = [COOKIE_ACCESS, COOKIE_REFRESH, COOKIE_USER]

  storageKeys.forEach((key) => {
    try {
      const item = localStorage.getItem(key)
      if (item) {
        const parsed = JSON.parse(item)
        cookies[key] = {
          value: maskToken(parsed.value),
          expires: parsed.expires ? new Date(parsed.expires).toISOString() : null,
          expired: parsed.expires ? Date.now() > parsed.expires : null,
        }
      } else {
        cookies[key] = { exists: false }
      }
    } catch (error) {
      cookies[key] = {
        error: 'parse_error',
        errorType: error instanceof Error ? error.name : 'unknown',
      }
    }
  })

  // Cookies del documento
  const documentCookies = ['hasSession', 'contrato', 'idUsuario', 'idCertificado', 'emailUsuario']

  documentCookies.forEach((name) => {
    const value = getCookieValue(name)
    cookies[name] = value ? { value } : { exists: false }
  })

  return cookies
}

/**
 * Obtiene el valor de una cookie del documento
 * @param {string} name - Nombre de la cookie
 * @returns {string|null} Valor de la cookie
 */
const getCookieValue = (name) => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) {
    return parts.pop().split(';').shift()
  }
  return null
}

/**
 * Extrae flags relevantes del payload de respuesta
 * @param {Object} payload - Payload de respuesta
 * @returns {Object} Flags relevantes
 */
const extractRelevantFlags = (payload) => {
  if (!payload || typeof payload !== 'object') return {}

  const flags = {}
  const relevantKeys = [
    'authenticated',
    'valid',
    'expired',
    'locked',
    'disabled',
    'requiresAction',
    'sessionValid',
    'tokenValid',
  ]

  relevantKeys.forEach((key) => {
    if (key in payload) {
      flags[key] = payload[key]
    }
  })

  return flags
}
